/** Pure helpers for presenting an AI-prefilled invoice extraction in the
 *  workbench (INP-634).
 *
 *  An extraction's `extracted_by` is either `"manual"` (a practitioner
 *  confirmed/typed the label) or `"ai:<parser>@<version>"` (a runtime
 *  prefill awaiting confirmation). The workbench badges the ai case as
 *  "AI-extracted — confirm" and flags a low row-level confidence so the
 *  reviewer looks harder before saving. */

/** Below this row-level confidence, the prefill is worth a closer look
 *  before confirming. Row confidence is the min across the required core
 *  facts (supplier / date / total), so 0.7 is the parser's "found via a
 *  header heuristic, not a labelled field" floor. */
export const LOW_CONFIDENCE_THRESHOLD = 0.7;

/** True when the extraction came from the runtime parser, not a human. */
export function isAiPrefill(extractedBy: string | null | undefined): boolean {
  return typeof extractedBy === 'string' && extractedBy.startsWith('ai:');
}

/** Row-level confidence, coerced to a number. Confidence arrives as a
 *  `Numeric(4,3)` which openapi serializes as a string. */
function confidenceValue(confidence: string | number | null | undefined): number | null {
  if (confidence === null || confidence === undefined) return null;
  const n = typeof confidence === 'number' ? confidence : Number(confidence);
  return Number.isFinite(n) ? n : null;
}

/** True when this is an AI prefill whose confidence is below the flag
 *  threshold (or has no confidence at all — an extraction with no scored
 *  core fact is inherently uncertain). Manual labels are never low. */
export function isLowConfidencePrefill(
  extractedBy: string | null | undefined,
  confidence: string | number | null | undefined,
): boolean {
  if (!isAiPrefill(extractedBy)) return false;
  const value = confidenceValue(confidence);
  return value === null || value < LOW_CONFIDENCE_THRESHOLD;
}
